import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, Lock, User, FileText, Eye, EyeOff } from 'lucide-react';
import ForgotPassword from './ForgotPassword';

/**
 * Einstiegsseite auf app.offfer.de — schlanke Login/Register-Karte.
 * KEIN Marketing hier (das liegt auf offfer.de / Astro).
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AppEntry = ({ onLogin }) => {
  // 'login' | 'register' | 'forgot'
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [login, setLogin]       = useState({ email: '', password: '' });
  const [reg, setReg]           = useState({ email: '', password: '', confirm: '', full_name: '', company_name: '' });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('register') === '1') setMode('register');
    else if (params.get('forgot') === '1') setMode('forgot');
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!login.email || !login.password) { toast.error('Bitte E-Mail und Passwort eingeben'); return; }
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(login),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Anmeldung fehlgeschlagen');
      const data = await r.json();
      localStorage.setItem('authToken', data.access_token);
      localStorage.setItem('userData', JSON.stringify(data.user));
      onLogin && onLogin(data.user);
      toast.success('Willkommen zurück!');
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!reg.email || !reg.password || !reg.full_name || !reg.company_name) {
      toast.error('Bitte alle Felder ausfüllen'); return;
    }
    if (reg.password !== reg.confirm) { toast.error('Passwörter stimmen nicht überein'); return; }
    if (!acceptedPrivacy) { toast.error('Bitte Datenschutz & AGB zustimmen'); return; }
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: reg.email, password: reg.password,
          full_name: reg.full_name, company_name: reg.company_name,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Registrierung fehlgeschlagen');
      const data = await r.json();
      localStorage.setItem('authToken', data.access_token);
      localStorage.setItem('userData', JSON.stringify(data.user));
      onLogin && onLogin(data.user);
      toast.success('Account erstellt — willkommen!');
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="https://offfer.de/" className="text-2xl font-bold tracking-tight text-slate-900">
            off<span style={{ color: '#f59e0b' }}>f</span>er
          </a>
          <a href="https://offfer.de/" className="text-sm text-slate-500 hover:text-slate-900">← zur Website</a>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
          {mode === 'forgot' ? (
            <>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">Passwort vergessen</h1>
              <p className="text-sm text-slate-500 mb-6">Wir senden dir einen Reset-Link.</p>
              <ForgotPassword onBack={() => setMode('login')} />
            </>
          ) : mode === 'register' ? (
            <>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">Kostenlos registrieren</h1>
              <p className="text-sm text-slate-500 mb-6">In 60 Sekunden eingerichtet.</p>
              <form onSubmit={handleRegister} className="space-y-3">
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input placeholder="Vollständiger Name" className="pl-10"
                    value={reg.full_name} onChange={e => setReg({ ...reg, full_name: e.target.value })} />
                </div>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input placeholder="Firmenname" className="pl-10"
                    value={reg.company_name} onChange={e => setReg({ ...reg, company_name: e.target.value })} />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input type="email" placeholder="E-Mail" className="pl-10"
                    value={reg.email} onChange={e => setReg({ ...reg, email: e.target.value })} />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input type={showPw ? 'text' : 'password'} placeholder="Passwort wählen" className="pl-10 pr-10"
                    value={reg.password} onChange={e => setReg({ ...reg, password: e.target.value })} />
                  <button type="button" className="absolute right-3 top-3 text-slate-400" onClick={() => setShowPw(!showPw)}>
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input type="password" placeholder="Passwort wiederholen" className="pl-10"
                    value={reg.confirm} onChange={e => setReg({ ...reg, confirm: e.target.value })} />
                </div>
                <label className="flex items-start gap-2 text-xs text-slate-600 mt-1">
                  <input type="checkbox" checked={acceptedPrivacy} onChange={e => setAcceptedPrivacy(e.target.checked)}
                    className="mt-0.5 accent-amber-500 cursor-pointer" />
                  <span>
                    Ich habe die <a href="https://offfer.de/datenschutz/" target="_blank" rel="noopener noreferrer" className="text-amber-600 underline">Datenschutzerklärung</a> gelesen und stimme den <a href="https://offfer.de/agb/" target="_blank" rel="noopener noreferrer" className="text-amber-600 underline">AGB</a> zu.
                  </span>
                </label>
                <Button type="submit" disabled={loading || !acceptedPrivacy} className="w-full bg-amber-500 hover:bg-amber-400 text-white mt-2">
                  {loading ? 'Registrierung...' : 'Kostenlos starten'}
                </Button>
              </form>
              <div className="mt-6 text-center text-sm text-slate-500">
                Bereits einen Account?{' '}
                <button onClick={() => setMode('login')} className="text-amber-600 underline font-medium">Anmelden</button>
              </div>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">Anmelden</h1>
              <p className="text-sm text-slate-500 mb-6">Mit deinem offfer-Account anmelden.</p>
              <form onSubmit={handleLogin} className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input type="email" placeholder="E-Mail" className="pl-10"
                    value={login.email} onChange={e => setLogin({ ...login, email: e.target.value })} />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input type={showPw ? 'text' : 'password'} placeholder="Passwort" className="pl-10 pr-10"
                    value={login.password} onChange={e => setLogin({ ...login, password: e.target.value })} />
                  <button type="button" className="absolute right-3 top-3 text-slate-400" onClick={() => setShowPw(!showPw)}>
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-400 text-white mt-2">
                  {loading ? 'Anmelden...' : 'Anmelden'}
                </Button>
              </form>
              <div className="flex justify-between text-sm mt-4">
                <button onClick={() => setMode('forgot')} className="text-slate-500 hover:text-slate-900">Passwort vergessen?</button>
                <button onClick={() => setMode('register')} className="text-amber-600 underline font-medium">Kostenlos starten</button>
              </div>
            </>
          )}
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white py-4">
        <div className="max-w-5xl mx-auto px-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-slate-500">
          <a href="https://offfer.de/impressum/" className="hover:text-slate-900">Impressum</a>
          <a href="https://offfer.de/datenschutz/" className="hover:text-slate-900">Datenschutz</a>
          <a href="https://offfer.de/agb/" className="hover:text-slate-900">AGB</a>
        </div>
      </footer>
    </div>
  );
};

export default AppEntry;
